import React, { useState, useEffect } from 'react';
import { 
  Activity, 
  ArrowRight, 
  ShieldCheck, 
  Cpu, 
  TrendingUp, 
  BarChart3, 
  LineChart, 
  Globe, 
  Lock, 
  CheckCircle2, 
  Zap, 
  Sparkles, 
  ChevronRight, 
  ChevronDown, 
  Sliders, 
  Bot, 
  AlertTriangle, 
  Eye, 
  Database, 
  Check, 
  Crown, 
  HelpCircle,
  Menu,
  X,
  ExternalLink,
  Layers,
  FileText,
  Clock,
  RefreshCw
} from 'lucide-react';
import { LandingLiveTicker } from './LandingLiveTicker';
import { LegalNoticeModal, LegalTabType } from './LegalNoticeModal';
import { fetchCompanyInfo, CompanyLegalInfo } from '../services/legalApi';
import { formatProductVersion } from '../utils/version';

interface LandingViewProps {
  onOpenAuth: (mode: 'login' | 'signup') => void;
}

export function LandingView({ onOpenAuth }: LandingViewProps) {
  const [uiLanguage, setUiLanguage] = useState<'ko' | 'en'>('ko');
  const [activePreviewTab, setActivePreviewTab] = useState<'dashboard' | 'macro' | 'portfolio' | 'autotrading'>('dashboard');
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLegalModalOpen, setIsLegalModalOpen] = useState(false);
  const [legalModalTab, setLegalModalTab] = useState<LegalTabType>('TERMS');
  const [companyInfo, setCompanyInfo] = useState<CompanyLegalInfo | null>(null);

  useEffect(() => {
    fetchCompanyInfo().then(data => {
      if (data) setCompanyInfo(data);
    }).catch(err => {
      console.warn('[LandingView] fetchCompanyInfo error:', err);
    });
  }, []);

  const openLegalModal = (tab: LegalTabType) => {
    setLegalModalTab(tab);
    setIsLegalModalOpen(true);
  };

  useEffect(() => {
    const handleUrlHashOrQuery = () => {
      const hash = window.location.hash.toLowerCase();
      const params = new URLSearchParams(window.location.search);
      const modalParam = params.get('modal')?.toLowerCase();

      if (hash === '#terms' || modalParam === 'terms') {
        openLegalModal('TERMS');
      } else if (hash === '#privacy' || modalParam === 'privacy') {
        openLegalModal('PRIVACY');
      } else if (hash === '#company' || modalParam === 'company') {
        openLegalModal('COMPANY');
      } else if (hash === '#disclaimer' || modalParam === 'disclaimer') {
        openLegalModal('DISCLAIMER');
      } else if (hash === '#chatrules' || modalParam === 'chatrules') {
        openLegalModal('CHAT_RULES');
      }
    };

    handleUrlHashOrQuery();
    window.addEventListener('hashchange', handleUrlHashOrQuery);
    return () => window.removeEventListener('hashchange', handleUrlHashOrQuery);
  }, []);

  const toggleLanguage = () => {
    setUiLanguage(prev => prev === 'ko' ? 'en' : 'ko');
  };

  const isLivePrivateHost = typeof window !== 'undefined' && (
    window.location.hostname.includes('ezoptix-pro') ||
    window.location.hostname.startsWith('pro.')
  );

  useEffect(() => {
    if (isLivePrivateHost) {
      let meta = document.querySelector('meta[name="robots"]');
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', 'robots');
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', 'noindex, nofollow, noarchive');
      document.title = 'EZOptix Pro | Private Terminal';
    } else {
      document.title = 'EZOptix | 모의투자 & 퀀트 인텔리전스';
    }
  }, [isLivePrivateHost]);

  const isKo = uiLanguage === 'ko';

  // 5 Tiers Data (Matching TierGate.tsx, Option B)
  const tiers = [
    {
      tierId: 'TIER1',
      name: 'Tier 1: Starter',
      price: isKo ? '무료' : 'Free',
      period: '',
      desc: isKo ? '시장 지표 독해의 기초를 다지고 안전한 모의투자로 실전 감각을 익히는 입문 단계' : 'Learn fundamentals of macro indicators with risk-free sandbox practice.',
      badge: null,
      highlight: false,
      color: 'slate',
      perks: isKo ? [
        '글로벌 매크로 지표 및 시장 레짐 기본 분석',
        '모의투자 (Sandbox) 기반 실전 투자 감각 학습',
        '관심 종목 실시간 모니터링',
        '핵심 시장 뉴스 AI 요약'
      ] : [
        'Global macro indicators & market regime analysis',
        'Risk-free Sandbox environment for hands-on practice',
        'Real-time watchlist symbols tracking',
        'Core market news AI summaries'
      ]
    },
    {
      tierId: 'TIER2',
      name: 'Tier 2: Growth',
      price: '₩9,900',
      period: isKo ? '/월' : '/mo',
      desc: isKo ? '클라우드 모의 포트폴리오 샌드박스로 자산 흐름을 추적하고 시장 변동성에 대응하는 단계' : 'Track simulated portfolio allocations in the cloud sandbox and navigate market volatility.',
      badge: null,
      highlight: false,
      color: 'amber',
      perks: isKo ? [
        '클라우드 모의 시드머니 기반 포트폴리오 정밀 진단',
        '실시간 통합 모의 포트폴리오 평가 및 시뮬레이션 추적',
        '긴급 매크로 변동성 및 리스크 알림 수신',
        '보유 종목별 맞춤 뉴스 영향도 분석'
      ] : [
        'Cloud seed capital based portfolio diagnostics',
        'Real-time unified mock portfolio evaluation',
        'Emergency macro risk & volatility alerts',
        'Tailored AI news impact scoring for holdings'
      ]
    },
    {
      tierId: 'TIER3',
      name: 'Tier 3: Core',
      price: '₩24,900',
      period: isKo ? '/월' : '/mo',
      desc: isKo ? '다양한 자산군을 체계적으로 배분하고 모의 리밸런싱 전략을 시뮬레이션하는 단계' : 'Multi-scenario asset allocation with disciplined simulation strategies.',
      badge: isKo ? '가장 인기 있는 플랜' : 'Most Popular',
      highlight: true,
      color: 'emerald',
      perks: isKo ? [
        '다중 모의 시나리오 통합 포트폴리오 관리',
        '목표 비중 기반 모의 리밸런싱 계산 가이드 (T+3 완충)',
        '자산군 분산 및 최대 낙폭 (MDD) 위험 통제',
        '실시간 시그널 웹훅 알림 & 퀀트 지표 연동'
      ] : [
        'Multi-scenario integrated mock portfolio management',
        'Target weight optimal rebalancing calculator (T+3 buffer)',
        'Asset class diversification & MDD risk control',
        'Eligibility for Signal Webhook & Quant Data integration'
      ]
    },
    {
      tierId: 'TIER4',
      name: 'Tier 4: Advanced',
      price: '₩49,000',
      period: isKo ? '/월' : '/mo',
      desc: isKo ? '공매도 및 옵션 파생 시장 지표를 접목하여 정밀한 투자 전략을 수립하는 단계' : 'Incorporate short-selling & option chain analytics for refined strategies.',
      badge: null,
      highlight: false,
      color: 'blue',
      perks: isKo ? [
        '맞춤형 AI 개인화 분석 및 심층 포트폴리오 진단',
        '공매도 추이 및 옵션 파생 시장 퀀트 지표 열람',
        '실시간 호가 지표 및 체결 비용 시뮬레이션',
        '시장 테마별 선행 지표 종합 모니터링'
      ] : [
        'Custom AI personalization & in-depth diagnostics',
        'Short-selling trends & options derivatives indicators',
        'Real-time quote metrics & execution cost simulation',
        'Leading indicators across major market themes'
      ]
    },
    {
      tierId: 'TIER5',
      name: 'Tier 5: Enterprise',
      price: '₩89,000',
      period: isKo ? '/월' : '/mo',
      desc: isKo ? '최상위 AI 모델과 시나리오 백테스트를 활용하여 독자적인 투자 시스템을 완성하는 단계' : 'Institutional power with frontier AI models, raw data & scenario backtests.',
      badge: isKo ? '전문 분석가용' : 'Professional',
      highlight: false,
      color: 'violet',
      perks: isKo ? [
        '최상위 생성형 AI 모델 기반 심층 시장 진단',
        '매크로 시나리오별 숏/헤징 시뮬레이션 백테스트',
        '실시간 금융 시계열 원천 데이터 분석',
        '1:1 전담 기술 지원 및 맞춤형 환경 설정'
      ] : [
        'Frontier generative AI model access for deep diagnostics',
        'Macro scenario short & hedging backtest simulations',
        'Real-time financial timeseries raw data access',
        '1:1 priority dedicated support & custom setup'
      ]
    }
  ];

  // FAQ list
  const faqs = [
    {
      q: isKo ? '초보 투자자도 바로 이용할 수 있나요?' : 'Can complete beginners start using EZOptix?',
      a: isKo 
        ? '네, 가능합니다. EZOptix는 무료 등급인 Tier 1 계정을 통해 가입 즉시 모의투자(Sandbox) 환경을 제공합니다. 실제 자금을 투입하지 않고도 매크로 지표 분석과 AI 포트폴리오 리밸런싱을 안전하게 시뮬레이션해 보실 수 있습니다.'
        : 'Yes. EZOptix provides a full Sandbox mock environment under the free Tier 1 plan. You can safely simulate macro regime analysis and portfolio rebalancing without risking real capital.'
    },
    {
      q: isKo ? '모의투자 시뮬레이션 환경 및 증권사 연동은 어떻게 지원되나요?' : 'How does the simulation environment and broker integration work?',
      a: isKo
        ? 'EZOptix는 별도 계좌 없이 즉시 시작하는 "클라우드 모의 샌드박스"와 국내 주요 증권사(한국투자증권·LS증권·키움증권)의 공식 "모의투자(VTS) OpenAPI" 연동을 모두 지원합니다. 두 방식 모두 실제 자금 투입이나 자산 인출 권한이 없는 100% 모의투자 환경으로, 안전하게 퀀트 전략을 시뮬레이션하고 검증할 수 있습니다.'
        : 'EZOptix supports both standalone Cloud Virtual Sandboxes and official domestic brokerage Paper Trading (VTS) OpenAPI connections (KIS, LS, Kiwoom). Both operate in strict non-custodial simulation modes without real capital or withdrawal authority.'
    },
    {
      q: isKo ? '실시간 시그널 레이더 알림은 어떻게 작동하나요?' : 'How do real-time signal radar alerts work?',
      a: isKo
        ? '매크로 레짐 신호(인플레이션, VIX 공포지수, 금리 변동성)와 사용자가 설정한 기술적 지표 조건을 실시간 연산하여, 조건 도달 시 메신저, 웹으로 즉각 알림을 발송합니다. 시스템이 임의로 자산을 매매하지 않으며 사용자가 직접 판단하여 학습할 수 있도록 객관적 연산 데이터를 제공합니다.'
        : 'Our engine evaluates macro signals (inflation, VIX volatility, yield curves) against your defined quantitative thresholds and dispatches instant alerts via messenger and web.'
    },
    {
      q: isKo ? '국내주식(KRX)과 미국주식(US)을 함께 관리할 수 있나요?' : 'Can I manage both US and Korean stocks together?',
      a: isKo
        ? '네. 단일 포트폴리오 내에서 미국 주식(달러 자산)과 국내 주식(원화 자산)을 통합 관리할 수 있습니다. 실시간 환율을 반영하여 총 평가자산 및 비중을 산출하고, 균형적인 자산 배분 비중을 계산해 드립니다.'
        : 'Yes. You can manage multi-currency portfolios containing both US stocks (USD) and KRX stocks (KRW). Real-time exchange rates are factored into total evaluation and optimal asset allocation calculations.'
    },
    {
      q: isKo ? '멤버십 구독을 해지하면 어떻게 되나요?' : 'What happens if I cancel my paid membership?',
      a: isKo
        ? '예약 해지를 선택하시면 결제 주기 만료일까지 현재 티어의 모든 프리미엄 기능을 온전히 이용하실 수 있으며, 만료 시점에 무료 플랜(Tier 1)으로 안전하게 자동 전환됩니다. 등록된 결제 수단은 안전하게 만료 처리됩니다.'
        : 'When selecting Scheduled Cancellation, you keep all benefits until the end of your billing period, after which your account seamlessly reverts to the free Tier 1 plan with no penalty.'
    },
    {
      q: isKo ? 'EZOptix는 투자자문이나 일임 서비스를 제공하나요?' : 'Does EZOptix provide investment advisory or discretionary services?',
      a: isKo
        ? '아닙니다. EZOptix는 「자본시장과 금융투자업에 관한 법률」상의 투자자문업, 투자일임업 또는 유사투자자문업을 영위하지 않습니다. 본 서비스는 이용자 본인의 독립적인 판단을 돕기 위한 객관적 금융 데이터 시각화, 모의 포트폴리오 샌드박스 시뮬레이션 및 퀀트 지표 연산 소프트웨어(SaaS)만을 제공합니다.'
        : 'No. EZOptix is a quantitative SaaS data and simulation platform and does not provide investment advisory or discretionary asset management. All simulations and metrics are for educational and analytical purposes only.'
    }
  ];

  // ─── 폐쇄형 프라이빗 터미널 전용 뷰 (isLivePrivateHost === true) ───
  if (isLivePrivateHost) {
    return (
      <div className="min-h-screen bg-[#050505] text-white selection:bg-cyan-500/30 selection:text-cyan-300 font-sans flex flex-col justify-between overflow-x-hidden">
        {/* Private GNB */}
        <header className="border-b border-white/10 bg-zinc-950/80 backdrop-blur-xl">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-black/60 border border-cyan-500/40 p-1 flex items-center justify-center shadow-lg shadow-cyan-500/20">
                <img src="/logo-emblem.png" alt="EZOptix" className="w-full h-full object-contain" />
              </div>
              <div className="flex flex-col">
                <span className="text-lg font-extrabold tracking-tight bg-gradient-to-r from-blue-400 via-cyan-300 to-white bg-clip-text text-transparent">
                  EZOptix <span className="text-xs font-mono text-cyan-400 font-normal">PRO</span>
                </span>
                <span className="text-[8px] font-mono tracking-widest text-zinc-500 uppercase -mt-0.5 font-semibold">
                  PRIVATE TERMINAL GATEWAY
                </span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <span className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-cyan-950/60 border border-cyan-800/40 text-[10px] font-mono text-cyan-300">
                <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
                RESTRICTED HOST
              </span>
              <button
                onClick={toggleLanguage}
                className="px-2.5 py-1.5 text-xs font-mono font-bold text-zinc-400 hover:text-white bg-white/5 hover:bg-white/10 rounded-lg border border-white/10 transition-all"
                title="Change Language"
              >
                {uiLanguage === 'ko' ? 'EN' : '한국어'}
              </button>
            </div>
          </div>
        </header>

        {/* Private Hero Gate */}
        <main className="flex-1 flex items-center justify-center px-4 py-16 sm:py-24 relative">
          {/* Subtle Ambient Glow */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[550px] h-[350px] bg-cyan-950/20 blur-[130px] rounded-full pointer-events-none" />

          <div className="max-w-xl w-full mx-auto text-center relative z-10">
            {/* Terminal Shield Emblem */}
            <div className="w-16 h-16 rounded-2xl bg-zinc-900/90 border border-cyan-500/30 flex items-center justify-center text-cyan-400 mx-auto mb-6 shadow-2xl shadow-cyan-500/10">
              <Lock className="w-8 h-8 text-cyan-400" />
            </div>

            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-zinc-900 border border-cyan-500/30 text-cyan-400 text-xs font-mono font-semibold mb-4">
              <ShieldCheck className="w-3.5 h-3.5" />
              <span>{isKo ? '폐쇄형 프라이빗 터미널' : 'CLOSED PRIVATE TERMINAL'}</span>
            </div>

            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white">
              EZOptix Private Terminal
            </h1>
            <p className="text-sm font-mono text-zinc-400 mt-2">
              Institutional & VIP Access Gateway
            </p>

            <div className="mt-6 p-5 rounded-2xl bg-zinc-900/60 border border-white/10 text-left text-xs text-zinc-300 leading-relaxed font-sans space-y-2">
              <p className="text-zinc-200 font-semibold flex items-center gap-1.5">
                <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
                {isKo ? '인가된 사용자 전용 안내' : 'Authorized Personnel Only'}
              </p>
              <p className="text-zinc-400 text-[11px] leading-relaxed">
                {isKo
                  ? '본 호스트는 사전 승인된 화이트리스트 사용자 및 기관 파트너 전용 프라이빗 게이트웨이입니다. 보안 및 규제 준수 규정에 따라 일반 가입은 차단되며, 공인된 계정에 한해 거래 채널이 활성화됩니다.'
                  : 'This terminal gateway is restricted to pre-approved institutional partners and whitelisted accounts. Public registration is closed. All session activities are continuously audited.'}
              </p>
            </div>

            {/* Login Action */}
            <div className="mt-8 flex flex-col gap-3">
              <button
                onClick={() => onOpenAuth('login')}
                className="w-full py-4 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-black font-extrabold text-sm rounded-xl shadow-xl shadow-cyan-500/20 transition-all hover:scale-[1.01] active:scale-[0.99] flex items-center justify-center gap-2"
              >
                <Lock className="w-4 h-4" />
                <span>{isKo ? '공인 계정 로그인 (Authorized Sign In)' : 'Authorized Terminal Sign In'}</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            {/* Security Spec Badges */}
            <div className="mt-10 grid grid-cols-2 gap-3 text-left font-mono text-[11px]">
              <div className="p-3 rounded-xl bg-zinc-900/40 border border-white/5">
                <span className="text-zinc-500 block text-[9px]">ENCRYPTION VAULT</span>
                <span className="text-cyan-300 font-semibold">AES-256 Cloudflare Vault</span>
              </div>
              <div className="p-3 rounded-xl bg-zinc-900/40 border border-white/5">
                <span className="text-zinc-500 block text-[9px]">ACCESS PROTOCOL</span>
                <span className="text-emerald-300 font-semibold">Whitelist IP & 2FA Required</span>
              </div>
              <div className="p-3 rounded-xl bg-zinc-900/40 border border-white/5">
                <span className="text-zinc-500 block text-[9px]">ISOLATION NODE</span>
                <span className="text-zinc-300 font-semibold">Dedicated Pro Gateway</span>
              </div>
              <div className="p-3 rounded-xl bg-zinc-900/40 border border-white/5">
                <span className="text-zinc-500 block text-[9px]">AUDIT LEVEL</span>
                <span className="text-amber-300 font-semibold">Immutable Session Logs</span>
              </div>
            </div>

            <p className="mt-6 text-[10px] text-zinc-500 font-mono">
              {isKo
                ? '※ 비인가된 접근 시도는 정보통신망법에 의해 법적 제재를 받을 수 있으며 보안 로그에 기록됩니다.'
                : '※ Unauthorized access attempts will be logged and subject to legal prosecution.'}
            </p>
          </div>
        </main>

        {/* Private Minimal Footer */}
        <footer className="border-t border-white/5 bg-zinc-950 py-6 text-zinc-500 text-xs font-mono">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <span className="text-white font-sans font-bold">EZOptix Pro</span>
              <span className="text-[10px] text-cyan-400 bg-cyan-950/60 border border-cyan-800/40 px-1.5 py-0.5 rounded">
                v{formatProductVersion(typeof __APP_PRODUCT_VERSION__ !== 'undefined' ? __APP_PRODUCT_VERSION__ : '1.0')}
              </span>
              <span>© 2026 {companyInfo?.companyName || 'Aidrix Inc.'}</span>
            </div>

            <div className="flex items-center gap-4 text-xs">
              <button onClick={() => openLegalModal('TERMS')} className="hover:text-zinc-300 transition-colors">
                {isKo ? '이용약관' : 'Terms'}
              </button>
              <button onClick={() => openLegalModal('PRIVACY')} className="hover:text-zinc-300 transition-colors">
                {isKo ? '개인정보처리방침' : 'Privacy'}
              </button>
              <button onClick={() => openLegalModal('COMPANY')} className="hover:text-cyan-400 transition-colors">
                {isKo ? '사업자정보' : 'Company'}
              </button>
              <button onClick={() => openLegalModal('DISCLAIMER')} className="hover:text-amber-400 transition-colors">
                {isKo ? '면책고지' : 'Disclaimer'}
              </button>
            </div>
          </div>
        </footer>

        {/* 종합 법적 고지 모달 */}
        <LegalNoticeModal
          isOpen={isLegalModalOpen}
          onClose={() => setIsLegalModalOpen(false)}
          initialTab={legalModalTab}
          uiLanguage={uiLanguage}
        />
      </div>
    );
  }

  // ─── 공개 모의투자 전용 뷰 (isLivePrivateHost === false) ───
  return (
    <div className="min-h-screen bg-[#050505] text-white selection:bg-emerald-500/30 selection:text-emerald-300 font-sans overflow-x-hidden">
      {/* ─── 1. Header / GNB ────────────────────────────────────────────────────────── */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-zinc-950/80 backdrop-blur-xl border-b border-white/10 transition-all">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          {/* Brand Logo */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-black/40 border border-cyan-500/30 p-1 flex items-center justify-center shadow-lg shadow-cyan-500/20">
              <img src="/logo-emblem.png" alt="EZOptix" className="w-full h-full object-contain" />
            </div>
            <div className="flex flex-col">
              <span className="text-xl font-extrabold tracking-tight bg-gradient-to-r from-blue-400 via-cyan-300 to-white bg-clip-text text-transparent">
                EZOptix
              </span>
              <span className="text-[9px] font-mono tracking-widest text-cyan-400 uppercase -mt-0.5 font-semibold">
                MACRO & QUANT INTELLIGENCE
              </span>
            </div>
          </div>

          {/* Desktop Nav Links */}
          <nav className="hidden md:flex items-center gap-7 text-sm font-medium text-zinc-400">
            <a href="#features" className="hover:text-white transition-colors">{isKo ? '주요 기능' : 'Features'}</a>
            <a href="#macro" className="hover:text-white transition-colors">{isKo ? '매크로 레짐' : 'Macro'}</a>
            <a href="#signal-radar" className="hover:text-white transition-colors">{isKo ? '시그널 레이더' : 'Signal Radar'}</a>
            <a href="#preview" className="hover:text-white transition-colors">{isKo ? '터미널 미리보기' : 'Preview'}</a>
            <a href="#pricing" className="hover:text-white transition-colors">{isKo ? '멤버십 (Tier 1~5)' : 'Pricing'}</a>
            <a href="#faq" className="hover:text-white transition-colors">{isKo ? 'FAQ' : 'FAQ'}</a>
          </nav>

          {/* Header Action Buttons */}
          <div className="flex items-center gap-3">
            {/* Language Switcher */}
            <button
              onClick={toggleLanguage}
              className="px-2.5 py-1.5 text-xs font-mono font-bold text-zinc-400 hover:text-white bg-white/5 hover:bg-white/10 rounded-lg border border-white/10 transition-all"
              title="Change Language"
            >
              {uiLanguage === 'ko' ? 'EN' : '한국어'}
            </button>

            {/* Login (Ghost) */}
            <button
              onClick={() => onOpenAuth('login')}
              className="hidden sm:inline-flex px-4 py-2 text-xs font-bold text-zinc-300 hover:text-white hover:bg-white/5 rounded-xl transition-all"
            >
              {isKo ? '로그인' : 'Sign In'}
            </button>

            {/* Start Free (Gradient CTA) */}
            <button
              onClick={() => onOpenAuth('signup')}
              className="flex items-center gap-1.5 px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-black text-xs font-bold rounded-xl shadow-lg shadow-emerald-500/20 transition-all hover:scale-[1.02]"
            >
              <span>{isKo ? '무료로 시작하기' : 'Get Started'}</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 text-zinc-400 hover:text-white rounded-lg bg-white/5"
              aria-label="Toggle Menu"
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Dropdown Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-zinc-950 border-b border-white/10 px-4 py-4 space-y-3">
            <a 
              href="#features" 
              onClick={() => setIsMobileMenuOpen(false)} 
              className="block text-sm text-zinc-400 hover:text-white py-1.5"
            >
              {isKo ? '주요 기능' : 'Features'}
            </a>
            <a 
              href="#macro" 
              onClick={() => setIsMobileMenuOpen(false)} 
              className="block text-sm text-zinc-400 hover:text-white py-1.5"
            >
              {isKo ? '매크로 레짐' : 'Macro'}
            </a>
            <a 
              href="#signal-radar" 
              onClick={() => setIsMobileMenuOpen(false)} 
              className="block text-sm text-zinc-400 hover:text-white py-1.5"
            >
              {isKo ? '시그널 레이더' : 'Signal Radar'}
            </a>
            <a 
              href="#pricing" 
              onClick={() => setIsMobileMenuOpen(false)} 
              className="block text-sm text-zinc-400 hover:text-white py-1.5"
            >
              {isKo ? '멤버십 (Tier 1~5)' : 'Pricing'}
            </a>
            <a 
              href="#faq" 
              onClick={() => setIsMobileMenuOpen(false)} 
              className="block text-sm text-zinc-400 hover:text-white py-1.5"
            >
              {isKo ? '자주 묻는 질문' : 'FAQ'}
            </a>
            <div className="pt-2 border-t border-white/10 flex gap-2">
              <button
                onClick={() => { setIsMobileMenuOpen(false); onOpenAuth('login'); }}
                className="flex-1 py-2 text-xs font-bold text-zinc-300 bg-white/5 rounded-lg text-center"
              >
                {isKo ? '로그인' : 'Sign In'}
              </button>
              <button
                onClick={() => { setIsMobileMenuOpen(false); onOpenAuth('signup'); }}
                className="flex-1 py-2 text-xs font-bold text-black bg-emerald-500 rounded-lg text-center"
              >
                {isKo ? '무료 가입' : 'Sign Up'}
              </button>
            </div>
          </div>
        )}
      </header>

      {/* ─── 2. Hero Section ────────────────────────────────────────────────────────── */}
      <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-24 overflow-hidden">
        {/* Ambient Glows */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[650px] h-[350px] bg-emerald-500/15 blur-[130px] rounded-full pointer-events-none" />
        <div className="absolute top-1/3 left-1/3 w-[450px] h-[250px] bg-cyan-500/10 blur-[110px] rounded-full pointer-events-none" />

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          {/* Top Pill Badge */}
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-zinc-900/90 border border-emerald-500/30 text-emerald-400 text-xs font-medium mb-8 backdrop-blur-md shadow-lg shadow-emerald-500/10 animate-in fade-in slide-in-from-bottom-3 duration-700">
            <Sparkles className="w-3.5 h-3.5" />
            <span className="font-mono tracking-tight">
              {isKo ? '시장 데이터 독해부터 AI 투자 전략까지 · 학습 및 성장형 투자 플랫폼' : 'From Market Data Literacy to AI Strategies · Learning & Growth Platform'}
            </span>
          </div>

          {/* Main Headline */}
          <h1 className="text-4xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.15] max-w-4xl mx-auto">
            {isKo ? (
              <>
                시장 데이터를 <span className="bg-gradient-to-r from-emerald-400 via-teal-300 to-cyan-400 bg-clip-text text-transparent">읽는 눈을 키우고</span>, AI와 함께 완성하는 나만의 투자
              </>
            ) : (
              <>
                Master Market Data, <span className="bg-gradient-to-r from-emerald-400 via-teal-300 to-cyan-400 bg-clip-text text-transparent">Grow Your Strategy</span> with AI Intelligence
              </>
            )}
          </h1>

          {/* Subtitle */}
          <p className="mt-6 text-base sm:text-xl text-zinc-400 max-w-2xl mx-auto leading-relaxed">
            {isKo
              ? '초보자를 위한 매크로 지표 독해부터 투명한 XAI 포트폴리오 최적화까지. 데이터에 기반해 스스로 판단하고 성장해가는 스마트한 투자 여정을 함께합니다.'
              : 'From beginner-friendly macro data literacy to transparent XAI portfolio optimization — empowering you to understand the market and grow with confidence.'}
          </p>

          {/* Dual CTAs */}
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 max-w-md mx-auto">
            <button
              onClick={() => onOpenAuth('signup')}
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-7 py-4 bg-emerald-500 hover:bg-emerald-400 text-black font-extrabold text-sm rounded-2xl shadow-xl shadow-emerald-500/25 transition-all hover:scale-[1.02] active:scale-[0.98]"
            >
              <span>{isKo ? '무료로 시작하기 (Tier 1: Starter)' : 'Start Free (Tier 1: Starter)'}</span>
              <ArrowRight className="w-4 h-4" />
            </button>

            <button
              onClick={() => onOpenAuth('login')}
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-7 py-4 bg-zinc-900/80 hover:bg-zinc-800 text-zinc-200 hover:text-white font-bold text-sm rounded-2xl border border-white/10 hover:border-white/20 transition-all"
            >
              <span>{isKo ? '기존 계정 로그인' : 'Terminal Sign In'}</span>
            </button>
          </div>

          {/* Trust Highlights */}
          <div className="mt-12 flex flex-wrap items-center justify-center gap-6 sm:gap-10 text-xs text-zinc-400 font-mono">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-400" />
              <span>{isKo ? '초보자를 위한 100% 무료 모의투자 (Sandbox)' : '100% Free Sandbox Practice'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4 text-cyan-400" />
              <span>{isKo ? 'AI 판단 근거를 투명하게 공개하는 Thinking Log' : 'Transparent XAI Thinking Logs'}</span>
            </div>
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-emerald-400" />
              <span>{isKo ? '4단계 리스크 가드레일 & 클라우드 모의 포트폴리오 샌드박스' : '4-Layer Risk Guardrails & Cloud Portfolio Sandbox'}</span>
            </div>
          </div>
        </div>
      </section>

      {/* ─── 3. Real-Time Macro Ticker Ribbon (SSOT API) ────────────────────────────── */}
      <section className="relative z-20">
        <LandingLiveTicker uiLanguage={uiLanguage} />
      </section>

      {/* ─── 4. Core Pillars (5대 핵심 기능 쇼케이스) ─────────────────────────────── */}
      <section id="features" className="py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-xs font-mono font-bold tracking-widest text-emerald-400 uppercase mb-3">
            {isKo ? 'LEARNING & GROWTH' : 'LEARNING & GROWTH'}
          </h2>
          <p className="text-3xl sm:text-4xl font-extrabold tracking-tight">
            {isKo ? '데이터 독해부터 실전 투자까지 단계별 성장 파이프라인' : 'Step-by-Step Growth Pipeline from Data Literacy to Execution'}
          </p>
          <p className="mt-4 text-zinc-400 text-sm sm:text-base">
            {isKo
              ? '단순한 차트 매매를 넘어, 거시경제 지표를 스스로 읽고 판단할 수 있는 힘을 길러주는 5대 핵심 인텔리전스를 제공합니다.'
              : 'Beyond simple charts: build your market data literacy and investment confidence with 5 core intelligence pillars.'}
          </p>
        </div>

        {/* Feature Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Card 1: Macro Regime */}
          <div id="macro" className="p-7 rounded-3xl bg-zinc-900/60 border border-zinc-800/80 hover:border-emerald-500/40 transition-all hover:-translate-y-1 group">
            <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400 mb-6 group-hover:scale-110 transition-transform">
              <Globe className="w-6 h-6" />
            </div>
            <h3 className="text-xl font-bold tracking-tight mb-2">
              {isKo ? '누구나 쉽게 이해하는 글로벌 매크로 레짐' : 'Understandable Global Macro Regimes'}
            </h3>
            <p className="text-sm text-zinc-400 leading-relaxed">
              {isKo
                ? '미국 연준(FRED)과 한국은행(ECOS)의 주요 거시 지표를 4대 경기 국면(골디락스, 인플레이션 등)으로 시각화하여, 경제 뉴스의 인과관계를 쉽게 이해하고 시장 흐름을 읽는 안목을 기릅니다.'
                : 'Visualizes complex macro indicators into 4 intuitive economic regimes to help you understand market cycles with ease.'}
            </p>
            <ul className="mt-5 space-y-2 text-xs font-mono text-zinc-400">
              <li className="flex items-center gap-2 text-emerald-400/90">
                <Check className="w-3.5 h-3.5" /> <span>{isKo ? '장단기 금리차 (10Y-2Y) 역전 및 정상화 해설' : '10Y-2Y Yield Inversion & Normalization Insights'}</span>
              </li>
              <li className="flex items-center gap-2 text-emerald-400/90">
                <Check className="w-3.5 h-3.5" /> <span>{isKo ? '실질 통화량(M2) & 유동성 흐름 독해' : 'Real M2 & Liquidity Trend Analysis'}</span>
              </li>
            </ul>
          </div>

          {/* Card 2: Quant Signal Webhook */}
          <div id="signal-radar" className="p-7 rounded-3xl bg-zinc-900/60 border border-zinc-800/80 hover:border-emerald-500/40 transition-all hover:-translate-y-1 group">
            <div className="w-12 h-12 rounded-2xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-400 mb-6 group-hover:scale-110 transition-transform">
              <Bot className="w-6 h-6" />
            </div>
            <h3 className="text-xl font-bold tracking-tight mb-2">
              {isKo ? '투명한 퀀트 시그널 레이더 & 실시간 알림' : 'Transparent Quant Signal Radar & Risk Engine'}
            </h3>
            <p className="text-sm text-zinc-400 leading-relaxed">
              {isKo
                ? '블랙박스 추천이 아닌, 사용자가 설정한 객관적 지표와 수식에 도달했을 때 즉각 메신저/웹으로 알려드립니다. 투명한 연산 과정(Calculation Log)을 통해 스스로 검증하세요.'
                : 'Inspect transparent quantitative calculation logs that trigger instant notifications via messenger or web whenever market conditions meet your criteria.'}
            </p>
            <ul className="mt-5 space-y-2 text-xs font-mono text-zinc-400">
              <li className="flex items-center gap-2 text-cyan-400/90">
                <Check className="w-3.5 h-3.5" /> <span>{isKo ? '투명한 지표 연산 과정 (Calculation Log) 공개' : 'Transparent Calculation Logs'}</span>
              </li>
              <li className="flex items-center gap-2 text-cyan-400/90">
                <Check className="w-3.5 h-3.5" /> <span>{isKo ? 'Layer 3 위험 지표 감지 안전망 탑재' : 'Layer 3 Risk Detection Stack Built-in'}</span>
              </li>
            </ul>
          </div>

          {/* Card 3: Dynamic Rebalancing */}
          <div className="p-7 rounded-3xl bg-zinc-900/60 border border-zinc-800/80 hover:border-emerald-500/40 transition-all hover:-translate-y-1 group">
            <div className="w-12 h-12 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-400 mb-6 group-hover:scale-110 transition-transform">
              <Sliders className="w-6 h-6" />
            </div>
            <h3 className="text-xl font-bold tracking-tight mb-2">
              {isKo ? '체계적인 자산 배분 & 퀀트 시뮬레이션' : 'Disciplined Asset Allocation & Simulation'}
            </h3>
            <p className="text-sm text-zinc-400 leading-relaxed">
              {isKo
                ? '목표 비중에 맞춘 규칙 기반 자산군 배분 전략을 시뮬레이션합니다. 거시 지표와 변동성을 반영한 통계적 모델링을 통해 포트폴리오의 리스크를 관리하고 균형 잡힌 자산 분산 원리를 학습합니다.'
                : 'Simulate disciplined, rule-based asset allocation strategies across macro regimes and manage portfolio volatility using statistical modeling.'}
            </p>
            <ul className="mt-5 space-y-2 text-xs font-mono text-zinc-400">
              <li className="flex items-center gap-2 text-amber-400/90">
                <Check className="w-3.5 h-3.5" /> <span>{isKo ? '포트폴리오 X-Ray (MDD 리스크 진단 및 방어)' : 'Portfolio X-Ray (MDD Risk Diagnostics)'}</span>
              </li>
              <li className="flex items-center gap-2 text-amber-400/90">
                <Check className="w-3.5 h-3.5" /> <span>{isKo ? '미국(US) + 국내(KR) 복합 자산군 균형 배분' : 'Balanced US & KR Multi-Asset Allocation'}</span>
              </li>
            </ul>
          </div>

          {/* Card 4: Global News Impact */}
          <div className="p-7 rounded-3xl bg-zinc-900/60 border border-zinc-800/80 hover:border-emerald-500/40 transition-all hover:-translate-y-1 group">
            <div className="w-12 h-12 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400 mb-6 group-hover:scale-110 transition-transform">
              <FileText className="w-6 h-6" />
            </div>
            <h3 className="text-xl font-bold tracking-tight mb-2">
              {isKo ? '글로벌 뉴스 AI 임팩트 & 경제 캘린더' : 'News AI Impact & Event Calendar'}
            </h3>
            <p className="text-sm text-zinc-400 leading-relaxed">
              {isKo
                ? '실시간 글로벌 금융 뉴스가 내 종목과 시장에 미치는 영향을 정량적으로 분석합니다. FOMC, CPI, 실적 발표 등 주요 경제 이벤트의 시장 파급 효과를 체계적으로 학습하세요.'
                : 'Quantitatively analyzes how financial headlines affect assets. Learn the market impact of FOMC meetings, CPI releases, and earnings.'}
            </p>
            <ul className="mt-5 space-y-2 text-xs font-mono text-zinc-400">
              <li className="flex items-center gap-2 text-indigo-400/90">
                <Check className="w-3.5 h-3.5" /> <span>{isKo ? '종목별 호재/악재 감성 스코어링 학습' : 'Per-asset Bull/Bear Sentiment Scoring'}</span>
              </li>
              <li className="flex items-center gap-2 text-indigo-400/90">
                <Check className="w-3.5 h-3.5" /> <span>{isKo ? '글로벌 경제 이벤트 D-Day 캘린더' : 'Global Economic Event Countdown'}</span>
              </li>
            </ul>
          </div>

          {/* Card 5: Option Chain & GEX */}
          <div className="p-7 rounded-3xl bg-zinc-900/60 border border-zinc-800/80 hover:border-emerald-500/40 transition-all hover:-translate-y-1 group">
            <div className="w-12 h-12 rounded-2xl bg-rose-500/10 border border-rose-500/20 flex items-center justify-center text-rose-400 mb-6 group-hover:scale-110 transition-transform">
              <LineChart className="w-6 h-6" />
            </div>
            <h3 className="text-xl font-bold tracking-tight mb-2">
              {isKo ? '옵션 체인 및 파생 데이터 센터' : 'Option Chain & Derivatives Center'}
            </h3>
            <p className="text-sm text-zinc-400 leading-relaxed">
              {isKo
                ? '투자 역량 성장에 따라 Max Pain 지점, Put/Call Ratio 등 기관 수급과 변동성 변곡점을 포착하는 고급 퀀트 지표로 분석 시야를 넓혀갈 수 있습니다.'
                : 'Broaden your analytical horizon with institutional metrics like Max Pain, Put/Call ratios, and gamma inflection points.'}
            </p>
            <ul className="mt-5 space-y-2 text-xs font-mono text-zinc-400">
              <li className="flex items-center gap-2 text-rose-400/90">
                <Check className="w-3.5 h-3.5" /> <span>{isKo ? '만기별 Max Pain 가격 계산 원리' : 'Expiration Max Pain Strike Calculation'}</span>
              </li>
              <li className="flex items-center gap-2 text-rose-400/90">
                <Check className="w-3.5 h-3.5" /> <span>{isKo ? 'GEX 기반 지지/저항 라인 시각화' : 'GEX-Driven Support & Resistance Levels'}</span>
              </li>
            </ul>
          </div>

          {/* Card 6: Enterprise Security */}
          <div className="p-7 rounded-3xl bg-zinc-900/60 border border-zinc-800/80 hover:border-emerald-500/40 transition-all hover:-translate-y-1 group">
            <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400 mb-6 group-hover:scale-110 transition-transform">
              <Lock className="w-6 h-6" />
            </div>
            <h3 className="text-xl font-bold tracking-tight mb-2">
              {isKo ? 'AES-256 엔터프라이즈 보안' : 'AES-256 Vault Architecture'}
            </h3>
            <p className="text-sm text-zinc-400 leading-relaxed">
              {isKo
                ? '사용자 설정 및 인증 정보는 브라우저에 노출되지 않으며 격리된 보안 환경에서 암호화 관리됩니다. 외부 금융 계좌 비밀번호나 자산 인출 권한을 절대 요구하지 않습니다.'
                : 'Sandbox configurations never touch local client cookies and are securely encrypted at rest. External account passwords and withdrawal privileges are never requested.'}
            </p>
            <ul className="mt-5 space-y-2 text-xs font-mono text-zinc-400">
              <li className="flex items-center gap-2 text-emerald-400/90">
                <Check className="w-3.5 h-3.5" /> <span>{isKo ? '독립형 클라우드 샌드박스 아키텍처' : 'Isolated cloud portfolio sandbox architecture'}</span>
              </li>
              <li className="flex items-center gap-2 text-emerald-400/90">
                <Check className="w-3.5 h-3.5" /> <span>{isKo ? '실시간 디바이스 세션 및 다중접속 보안 제어' : 'Concurrent device session security'}</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* ─── 5. Interactive Platform Showcase (터미널 미리보기) ────────────────────── */}
      <section id="preview" className="py-20 bg-zinc-950/60 border-y border-white/5 relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-xs font-mono font-bold tracking-widest text-emerald-400 uppercase mb-3">
              {isKo ? 'TERMINAL PREVIEW' : 'TERMINAL PREVIEW'}
            </h2>
            <p className="text-3xl font-extrabold tracking-tight">
              {isKo ? '전문가용 터미널을 직접 미리 경험해보세요' : 'Experience the Professional Terminal Interface'}
            </p>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap items-center justify-center gap-2 mb-8">
            {[
              { id: 'dashboard', labelKo: '종합 대시보드', labelEn: 'Dashboard' },
              { id: 'macro', labelKo: '매크로 인텔리전스', labelEn: 'Macro Regime' },
              { id: 'portfolio', labelKo: '자산 배분 시뮬레이션', labelEn: 'Asset Allocation Model' },
              { id: 'autotrading', labelKo: '시그널 레이더 알림', labelEn: 'Signal Radar' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActivePreviewTab(tab.id as any)}
                className={`px-5 py-2.5 rounded-xl text-xs font-bold transition-all ${
                  activePreviewTab === tab.id
                    ? 'bg-emerald-500 text-black shadow-lg shadow-emerald-500/20'
                    : 'bg-white/5 text-zinc-400 hover:text-white hover:bg-white/10 border border-white/5'
                }`}
              >
                {isKo ? tab.labelKo : tab.labelEn}
              </button>
            ))}
          </div>

          {/* Terminal Window Mockup */}
          <div className="rounded-3xl border border-white/10 bg-zinc-950 shadow-2xl overflow-hidden">
            {/* Window Topbar */}
            <div className="px-5 py-3.5 bg-zinc-900/90 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-rose-500/80" />
                <div className="w-3 h-3 rounded-full bg-amber-500/80" />
                <div className="w-3 h-3 rounded-full bg-emerald-500/80" />
                <span className="ml-3 font-mono text-[11px] text-zinc-500 font-medium">
                  terminal.macrooptix.io — {activePreviewTab.toUpperCase()}_VIEW
                </span>
              </div>
              <div className="flex items-center gap-2 text-[10px] font-mono text-emerald-400 bg-emerald-500/10 px-2.5 py-1 rounded-md border border-emerald-500/20">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                <span>ONLINE · D1 SYNC ACTIVE</span>
              </div>
            </div>

            {/* Mock Content Based on Active Tab */}
            <div className="p-6 sm:p-8 min-h-[380px] flex flex-col justify-center">
              {activePreviewTab === 'dashboard' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div className="p-4 rounded-2xl bg-zinc-900/70 border border-white/5">
                      <span className="text-xs text-zinc-500 font-mono">MACRO REGIME</span>
                      <p className="text-lg font-bold text-emerald-400 mt-1">골디락스 (Goldilocks)</p>
                      <span className="text-[11px] text-zinc-400 font-mono">Risk Score: 38/100</span>
                    </div>
                    <div className="p-4 rounded-2xl bg-zinc-900/70 border border-white/5">
                      <span className="text-xs text-zinc-500 font-mono">PORTFOLIO VALUE</span>
                      <p className="text-lg font-bold text-white mt-1">₩ 142,500,000</p>
                      <span className="text-[11px] text-emerald-400 font-mono">+1.85% (일간 손익)</span>
                    </div>
                    <div className="p-4 rounded-2xl bg-zinc-900/70 border border-white/5">
                      <span className="text-xs text-zinc-500 font-mono">FEAR & GREED</span>
                      <p className="text-lg font-bold text-amber-400 mt-1">68 (탐욕/Greed)</p>
                      <span className="text-[11px] text-zinc-400 font-mono">S&P500 수급 과열 경계</span>
                    </div>
                    <div className="p-4 rounded-2xl bg-zinc-900/70 border border-white/5">
                      <span className="text-xs text-zinc-500 font-mono">AUTO AGENT</span>
                      <p className="text-lg font-bold text-cyan-400 mt-1">ACTIVE (정상 감시)</p>
                      <span className="text-[11px] text-zinc-400 font-mono">VIX Safe Zone (&lt; 20)</span>
                    </div>
                  </div>
                  <div className="p-5 rounded-2xl bg-zinc-900/40 border border-white/5 font-mono text-xs text-zinc-400 space-y-2">
                    <div className="text-emerald-400 font-bold flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5" />
                      <span>AI Market Quick Scan Insight</span>
                    </div>
                    <p className="leading-relaxed">
                      "최근 미 국채 10년물 금리가 4.2%선에서 안정세를 보이며 기술주 중심의 상승 탄력이 유지되고 있습니다. 모의 포트폴리오의 나스닥 비중이 목표치를 4.2%p 초과하고 있어, T+3 관점의 비중 편차 보정 및 방어 자산군 모의 리밸런싱 시나리오가 산출되었습니다."
                    </p>
                  </div>
                </div>
              )}

              {activePreviewTab === 'macro' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 font-mono text-xs">
                    <div className="p-4 rounded-2xl bg-zinc-900/70 border border-white/5">
                      <span className="text-zinc-500">FED FUNDS RATE</span>
                      <p className="text-xl font-bold text-white mt-1">5.25% - 5.50%</p>
                      <p className="text-[11px] text-zinc-400 mt-1">차기 FOMC 동결 확률: 84.5%</p>
                    </div>
                    <div className="p-4 rounded-2xl bg-zinc-900/70 border border-white/5">
                      <span className="text-zinc-500">10Y - 2Y SPREAD</span>
                      <p className="text-xl font-bold text-emerald-400 mt-1">+0.18%p</p>
                      <p className="text-[11px] text-zinc-400 mt-1">장단기 금리차 정상화 국면</p>
                    </div>
                    <div className="p-4 rounded-2xl bg-zinc-900/70 border border-white/5">
                      <span className="text-zinc-500">US REAL M2 GROWTH</span>
                      <p className="text-xl font-bold text-cyan-400 mt-1">+1.4% YoY</p>
                      <p className="text-[11px] text-zinc-400 mt-1">실질 통화 유동성 완만한 확장세</p>
                    </div>
                  </div>
                  <div className="p-4 rounded-2xl bg-emerald-500/5 border border-emerald-500/20 text-xs text-zinc-300 font-mono">
                    <span className="text-emerald-400 font-bold">[Regime Allocation Directive]</span>
                    <p className="mt-1 leading-relaxed">
                      현재 매크로 체제(골디락스)에서는 주식 비중을 65~70%로 유지하고, 채권 20%, 원자재 및 현금을 10% 수준으로 분산 배분하여 변동성 위험을 통제합니다.
                    </p>
                  </div>
                </div>
              )}

              {activePreviewTab === 'portfolio' && (
                <div className="space-y-4 font-mono text-xs">
                  <div className="flex justify-between items-center pb-2 border-b border-white/10">
                    <span className="text-zinc-400 font-bold">QUANT ASSET ALLOCATION SIMULATOR</span>
                    <span className="text-emerald-400">MODEL: RISK PARITY & SHARPE</span>
                  </div>
                  <div className="space-y-2.5">
                    <div className="flex items-center justify-between p-3.5 rounded-xl bg-zinc-900/80 border border-white/5">
                      <div>
                        <span className="text-white font-bold">글로벌 주식군 (US/KR Equity Index)</span>
                        <p className="text-zinc-400 text-[11px] mt-0.5">목표 비중 55.0% vs 현재 시뮬레이션 59.8% (+4.8%p 괴리)</p>
                      </div>
                      <span className="px-2.5 py-1 rounded bg-cyan-500/20 text-cyan-300 font-bold text-[11px]">
                        변동성 완화 모델링: 비중 조정 연산
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3.5 rounded-xl bg-zinc-900/80 border border-white/5">
                      <div>
                        <span className="text-white font-bold">중장기 국채군 (US Treasury 10Y)</span>
                        <p className="text-zinc-400 text-[11px] mt-0.5">목표 비중 30.0% vs 현재 시뮬레이션 27.2% (-2.8%p 괴리)</p>
                      </div>
                      <span className="px-2.5 py-1 rounded bg-emerald-500/20 text-emerald-300 font-bold text-[11px]">
                        금리 상관성 방어: 포트폴리오 헤징
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3.5 rounded-xl bg-zinc-900/80 border border-white/5">
                      <div>
                        <span className="text-white font-bold">유동성 및 원자재 (Cash & Commodity)</span>
                        <p className="text-zinc-400 text-[11px] mt-0.5">목표 비중 15.0% vs 현재 시뮬레이션 13.0% (-2.0%p 괴리)</p>
                      </div>
                      <span className="px-2.5 py-1 rounded bg-amber-500/20 text-amber-300 font-bold text-[11px]">
                        MDD 완충 버퍼: 유동성 비중 충족
                      </span>
                    </div>
                  </div>
                  <div className="p-3 rounded-lg bg-black/40 border border-white/5 text-[10px] text-zinc-500 font-sans leading-relaxed">
                    ※ 본 시뮬레이션 화면은 통계적 자산 배분 모델의 계산 결과를 예시하는 교육용 도구이며, 개별 종목의 매수·매도를 추천하거나 투자 자문을 제공하지 않습니다.
                  </div>
                </div>
              )}

              {activePreviewTab === 'autotrading' && (
                <div className="space-y-4 font-mono text-xs">
                  <div className="p-3 rounded-xl bg-zinc-900/80 border border-white/10 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-ping" />
                      <span className="font-bold text-white">AGENT_ID: VIX_MACRO_SENTINEL_01</span>
                    </div>
                    <span className="text-emerald-400 font-bold">STATUS: LAYER 1~4 ALL SAFE</span>
                  </div>
                  <div className="p-4 rounded-xl bg-zinc-900/50 border border-white/5 text-zinc-300 space-y-2">
                    <p className="text-emerald-400 font-bold">[Quant Calculation Process Log]</p>
                    <p className="text-zinc-400 leading-relaxed text-[11px]">
                      1. [Macro Check] VIX 지수 14.85 유지 (임계치 25.0 이하 정상)<br />
                      2. [Volume Flow] 글로벌 멀티에셋 수급 및 거래량 모멘텀 정합성 확인<br />
                      3. [Signal Plan] 기술 지표(골든크로스) 조건 충족 ➔ 메신저 알림 발송 완료<br />
                      4. [Risk Shield] 잔여 리스크 한도 94.2% 유지. 다음 스캔 15분 후 예정.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ─── 6. Cloud Sandbox & Safety Stack ───────────────────────────────────────────── */}
      <section className="py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="p-8 sm:p-12 rounded-3xl bg-gradient-to-b from-zinc-900/80 to-zinc-950 border border-white/10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-400 text-xs font-mono font-bold mb-4 border border-emerald-500/20">
                <Database className="w-3.5 h-3.5" />
                <span>CLOUD SANDBOX &amp; QUANT ENGINE</span>
              </div>
              <h2 className="text-3xl font-extrabold tracking-tight">
                {isKo ? '외부 계좌 연동 없는 독립형 클라우드 모의투자 샌드박스' : 'Isolated Cloud Portfolio Sandbox Without External Account Linking'}
              </h2>
              <p className="mt-4 text-zinc-400 text-sm sm:text-base leading-relaxed">
                {isKo
                  ? '외부 금융 계좌 연동이나 실제 자금 투입 없이, 독립형 클라우드 모의 시드머니 환경을 통해 글로벌 퀀트 지표와 시장 시그널을 안전하게 검증하고 모의 포트폴리오를 시뮬레이션할 수 있습니다.'
                  : 'Validate quantitative indicators and simulate portfolio strategies safely in a risk-free cloud seed capital sandbox without linking external brokerage accounts.'}
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <span className="px-3.5 py-1.5 rounded-xl bg-white/5 border border-white/10 text-xs font-bold text-zinc-300">
                  {isKo ? '클라우드 모의 시드머니 샌드박스' : 'Cloud Seed Capital Sandbox'}
                </span>
                <span className="px-3.5 py-1.5 rounded-xl bg-white/5 border border-white/10 text-xs font-bold text-zinc-300">
                  {isKo ? '글로벌 시세 파이프라인 연동' : 'Global Market Data Pipeline'}
                </span>
                <span className="px-3.5 py-1.5 rounded-xl bg-white/5 border border-white/10 text-xs font-bold text-zinc-300">
                  {isKo ? 'US / KR 멀티마켓 통합 지원' : 'Unified US & KR Market Coverage'}
                </span>
              </div>
            </div>

            {/* 4-Layer Safety Stack */}
            <div className="space-y-3 font-mono text-xs">
              <div className="p-4 rounded-2xl bg-zinc-900/90 border border-white/5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-emerald-500/10 text-emerald-400 flex items-center justify-center font-bold">
                    L1
                  </div>
                  <div>
                    <p className="font-bold text-white">Layer 1: 모의 시드머니 잔고 및 시세 데이터 검증</p>
                    <p className="text-zinc-500 text-[10px]">모의 예수금 한도 및 포트폴리오 비중 정밀 대조</p>
                  </div>
                </div>
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              </div>

              <div className="p-4 rounded-2xl bg-zinc-900/90 border border-white/5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-cyan-500/10 text-cyan-400 flex items-center justify-center font-bold">
                    L2
                  </div>
                  <div>
                    <p className="font-bold text-white">Layer 2: 모의 체결 슬리피지 모델링</p>
                    <p className="text-zinc-500 text-[10px]">이론가 대비 0.1% 이상 불리한 시뮬레이션 오차 방지</p>
                  </div>
                </div>
                <CheckCircle2 className="w-4 h-4 text-cyan-400" />
              </div>

              <div className="p-4 rounded-2xl bg-zinc-900/90 border border-white/5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-amber-500/10 text-amber-400 flex items-center justify-center font-bold">
                    L3
                  </div>
                  <div>
                    <p className="font-bold text-white">Layer 3: 60초 데이터 파이프라인 서킷 브레이커</p>
                    <p className="text-zinc-500 text-[10px]">외부 시세 피드 3회 지연 시 즉각 1분간 백업 큐 우회</p>
                  </div>
                </div>
                <CheckCircle2 className="w-4 h-4 text-amber-400" />
              </div>

              <div className="p-4 rounded-2xl bg-zinc-900/90 border border-white/5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-purple-500/10 text-purple-400 flex items-center justify-center font-bold">
                    L4
                  </div>
                  <div>
                    <p className="font-bold text-white">Layer 4: 모의 포트폴리오 위험도(MDD) 통제</p>
                    <p className="text-zinc-500 text-[10px]">일간 최대 허용 변동폭 초과 시 시그널 알림 및 모의 분석 일시 정지</p>
                  </div>
                </div>
                <CheckCircle2 className="w-4 h-4 text-purple-400" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── 7. Membership & Tier Overview (Tier 1 ~ Tier 5) ───────────────────────── */}
      <section id="pricing" className="py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-xs font-mono font-bold tracking-widest text-emerald-400 uppercase mb-3">
            {isKo ? 'MEMBERSHIP TIERS' : 'MEMBERSHIP TIERS'}
          </h2>
          <p className="text-3xl sm:text-4xl font-extrabold tracking-tight">
            {isKo ? '단계별 투자 역량을 확장하는 5단계 멤버십' : '5 Progressive Tiers Tailored to Your Growth'}
          </p>
          <p className="mt-4 text-zinc-400 text-sm sm:text-base">
            {isKo
              ? '무료 플랜인 Tier 1: Starter에서 모의투자와 매크로 데이터 학습으로 시작하여, 투자 역량과 포트폴리오 성장에 맞춰 단계별로 확장해 보세요.'
              : 'Begin with Tier 1: Starter for free sandbox practice, and expand progressively as your strategy matures.'}
          </p>
        </div>

        {/* 5 Tiers Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {tiers.map((tier) => (
            <div
              key={tier.tierId}
              className={`p-6 rounded-3xl flex flex-col justify-between transition-all duration-300 relative ${
                tier.highlight
                  ? 'bg-zinc-900/90 border-2 border-emerald-500 shadow-2xl shadow-emerald-500/15 lg:-translate-y-2'
                  : 'bg-zinc-900/50 border border-white/10 hover:border-white/20'
              }`}
            >
              {/* Badge */}
              {tier.badge && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-emerald-500 text-black font-extrabold text-[10px] tracking-wide uppercase shadow-md">
                  {tier.badge}
                </div>
              )}

              <div>
                <h3 className="text-base font-bold text-white mb-1">
                  {tier.name}
                </h3>
                {/* 비회원 가격 노출 (토스페이 심사 필수 요건) */}
                <div className="flex items-baseline gap-1 my-3">
                  <span className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                    {tier.price}
                  </span>
                  {tier.period && (
                    <span className="text-xs text-zinc-400 font-medium">
                      {tier.period}
                    </span>
                  )}
                  {tier.price !== '무료' && tier.price !== 'Free' && (
                    <span className="text-[10px] text-zinc-500 font-sans ml-1">
                      (VAT 포함)
                    </span>
                  )}
                </div>
                <p className="text-xs text-zinc-400 min-h-[36px] leading-snug mb-4 pb-4 border-b border-white/10">
                  {tier.desc}
                </p>

                {/* Perks List */}
                <ul className="space-y-2.5 text-xs text-zinc-300 mb-6">
                  {tier.perks.map((perk, pIdx) => (
                    <li key={pIdx} className="flex items-start gap-2">
                      <Check className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${tier.highlight ? 'text-emerald-400' : 'text-zinc-500'}`} />
                      <span className="leading-tight text-[11px]">{perk}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Action Button */}
              <button
                onClick={() => onOpenAuth('signup')}
                className={`w-full py-3 rounded-xl text-xs font-bold transition-all ${
                  tier.highlight
                    ? 'bg-emerald-500 hover:bg-emerald-400 text-black shadow-lg shadow-emerald-500/20'
                    : 'bg-white/10 hover:bg-white/20 text-white'
                }`}
              >
                {tier.tierId === 'TIER1' ? (isKo ? '무료로 시작하기' : 'Start Free') : (isKo ? '가입 후 이용하기' : 'Get Started')}
              </button>
            </div>
          ))}
        </div>

        {/* 무형 상품 서비스 제공 방식, 유효기간 및 환불 규정 상세 고지 (토스페이먼츠 심사 필수 요건) */}
        <div className="mt-12 bg-zinc-900/60 border border-white/10 rounded-2xl p-6 sm:p-8 space-y-6">
          <div className="flex items-center gap-2 border-b border-white/10 pb-4">
            <ShieldCheck className="w-5 h-5 text-emerald-400" />
            <h3 className="text-sm sm:text-base font-bold text-white">
              {isKo ? '디지털 서비스(무형 상품) 제공 기준 및 이용자 보호 규정' : 'Digital Service Delivery & Refund Policy'}
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-xs text-zinc-300 leading-relaxed font-sans">
            {/* 1. 서비스 제공 방식 및 주기 */}
            <div className="p-4 rounded-xl bg-black/40 border border-white/5 space-y-2">
              <div className="flex items-center gap-2 text-emerald-400 font-bold">
                <Zap className="w-4 h-4" />
                <span>{isKo ? '서비스 제공 방식 및 제공 주기 (유효기간)' : 'Delivery Method & Validity Period'}</span>
              </div>
              <ul className="space-y-1.5 text-zinc-400 text-[11px] list-disc list-inside">
                <li>
                  <strong className="text-zinc-200">{isKo ? '제공 방식:' : 'Delivery:'}</strong> {isKo ? '온라인 무형 디지털 소프트웨어(SaaS)로, 결제 완료 즉시 로그인 계정에 해당 플랜 권한이 자동 부여되어 웹 대시보드(포트폴리오 분석, 퀀트 지표 등)에서 즉시 이용하실 수 있습니다. (별도 배송/오프라인 절차 없음)' : 'Instant digital activation upon payment; accessible via web dashboard.'}
                </li>
                <li>
                  <strong className="text-zinc-200">{isKo ? '제공 주기:' : 'Period:'}</strong> {isKo ? '월간 구독(30일) 단위 또는 연간 플랜(365일) 단위로 제공되며, 차기 결제일 전 언제든지 마이페이지에서 해지 가능합니다.' : 'Monthly (30 days) or Annual (365 days) subscription.'}
                </li>
                <li>
                  <strong className="text-emerald-400">{isKo ? '유효기간 준수:' : 'Validity:'}</strong> {isKo ? '모든 유료 서비스 및 이용권의 최종 서비스 제공 완료 기간(유효기간)은 결제일로부터 최대 1년 이내이며, 1년을 초과하지 않습니다.' : 'All services have a maximum validity period of up to 1 year.'}
                </li>
              </ul>
            </div>

            {/* 2. 청약철회 및 환불 규정 */}
            <div className="p-4 rounded-xl bg-black/40 border border-white/5 space-y-2">
              <div className="flex items-center gap-2 text-cyan-400 font-bold">
                <RefreshCw className="w-4 h-4" />
                <span>{isKo ? '청약철회 및 환불 규정 (판매자 귀책 및 부분환불)' : 'Cancellation & Refund Regulations'}</span>
              </div>
              <ul className="space-y-1.5 text-zinc-400 text-[11px] list-disc list-inside">
                <li>
                  <strong className="text-zinc-200">{isKo ? '단순 변심:' : 'General:'}</strong> {isKo ? '디지털 콘텐츠 특성상 결제 후 서비스 이용(데이터 조회, 실시간 분석 기능 사용 등)을 개시하지 않은 경우 7일 이내 청약철회가 가능합니다.' : 'Full refund within 7 days if no service features have been utilized.'}
                </li>
                <li>
                  <strong className="text-cyan-400">{isKo ? '판매자 귀책 사유 7일 환불:' : 'Seller Fault:'}</strong> {isKo ? '시스템 오류, 지속적인 서버 장애 등 회사의 귀책 사유로 정상 서비스를 이용하지 못한 경우 결제일로부터 7일 이내 100% 전액 환불됩니다.' : '100% full refund within 7 days in case of service defect or server failure.'}
                </li>
                <li>
                  <strong className="text-zinc-200">{isKo ? '중도 해지 부분환불:' : 'Partial Refund:'}</strong> {isKo ? '정기구독 및 기간 이용권의 중도 해지 시, 실제 이용 일수에 대한 정가 일할 금액 및 금융 결제 수수료를 공제한 잔여 일할 금액에 대해 부분환불이 가능합니다.' : 'Prorated partial refund available upon early cancellation.'}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ─── 8. FAQ Section ───────────────────────────────────────────────────────── */}
      <section id="faq" className="py-20 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-xs font-mono font-bold tracking-widest text-emerald-400 uppercase mb-3">
            {isKo ? 'FREQUENTLY ASKED QUESTIONS' : 'FREQUENTLY ASKED QUESTIONS'}
          </h2>
          <p className="text-3xl font-extrabold tracking-tight">
            {isKo ? '자주 묻는 질문' : 'Frequently Asked Questions'}
          </p>
        </div>

        <div className="space-y-3">
          {faqs.map((faq, index) => {
            const isOpen = openFaqIndex === index;
            return (
              <div
                key={index}
                className="rounded-2xl bg-zinc-900/60 border border-white/10 overflow-hidden transition-colors"
              >
                <button
                  onClick={() => setOpenFaqIndex(isOpen ? null : index)}
                  className="w-full p-5 text-left flex items-center justify-between gap-4 font-semibold text-sm hover:text-emerald-400 transition-colors"
                >
                  <span>{faq.q}</span>
                  <ChevronDown className={`w-4 h-4 text-zinc-400 shrink-0 transition-transform ${isOpen ? 'rotate-180 text-emerald-400' : ''}`} />
                </button>
                {isOpen && (
                  <div className="px-5 pb-5 text-xs text-zinc-400 leading-relaxed border-t border-white/5 pt-3 animate-in fade-in duration-200">
                    {faq.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* ─── 9. Final Conversion CTA Banner ───────────────────────────────────────── */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/20 via-zinc-950 to-zinc-950 pointer-events-none" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400 mx-auto mb-6 shadow-lg shadow-emerald-500/10">
            <Zap className="w-7 h-7" />
          </div>
          <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight">
            {isKo ? '오늘부터 데이터와 AI로 투자 기준을 바꾸세요' : 'Elevate Your Investment Standards with Macro AI'}
          </h2>
          <p className="mt-4 text-zinc-400 text-sm sm:text-base max-w-xl mx-auto">
            {isKo
              ? '카드 등록 없이 무료 Tier 1 계정으로 즉시 가입하고, 실시간 글로벌 매크로 지표와 포트폴리오 최적화 엔진을 직접 확인하세요.'
              : 'Sign up for free without a credit card and start exploring live macro telemetry and portfolio optimization instantly.'}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
            <button
              onClick={() => onOpenAuth('signup')}
              className="px-8 py-4 bg-emerald-500 hover:bg-emerald-400 text-black font-extrabold text-sm rounded-2xl shadow-xl shadow-emerald-500/25 transition-all hover:scale-[1.02]"
            >
              {isKo ? '무료로 EZOptix 시작하기 (Tier 1)' : 'Get Started Free with Tier 1'}
            </button>
            <button
              onClick={() => onOpenAuth('login')}
              className="px-8 py-4 bg-white/5 hover:bg-white/10 text-white font-bold text-sm rounded-2xl border border-white/10 transition-all"
            >
              {isKo ? '터미널 로그인' : 'Terminal Sign In'}
            </button>
          </div>
        </div>
      </section>

      {/* ─── 10. Footer ───────────────────────────────────────────────────────────── */}
      <footer className="py-12 border-t border-white/10 bg-zinc-950 text-zinc-500 text-xs font-mono">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 rounded-lg bg-black/40 border border-cyan-500/30 p-0.5 flex items-center justify-center">
              <img src="/logo-emblem.png" alt="EZOptix" className="w-full h-full object-contain" />
            </div>
            <span className="font-bold text-white font-sans text-sm">EZOptix</span>
            <div className="relative group inline-flex items-center">
              <span className="text-[10px] font-mono text-cyan-400 bg-cyan-950/60 border border-cyan-800/40 px-1.5 py-0.5 rounded cursor-default select-none">
                v{formatProductVersion(typeof __APP_PRODUCT_VERSION__ !== 'undefined' ? __APP_PRODUCT_VERSION__ : '1.0')}
              </span>
              {/* 즉각 반응형 인라인 말풍선 툴팁 */}
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:flex flex-col items-center z-50 pointer-events-none">
                <div className="bg-zinc-900 border border-cyan-500/30 text-cyan-300 text-[10px] font-mono px-2 py-1 rounded-md shadow-xl whitespace-nowrap">
                  Build {typeof __APP_BUILD_VERSION__ !== 'undefined' ? __APP_BUILD_VERSION__ : '1.135.0'}
                </div>
                <div className="w-1.5 h-1.5 bg-zinc-900 border-r border-b border-cyan-500/30 rotate-45 -mt-1" />
              </div>
            </div>
            <span>© 2026 {companyInfo?.companyName || (isKo ? '에이드릭스 (Aidrix Inc.)' : 'Aidrix Inc.')}. All rights reserved.</span>
          </div>

          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs">
            <a href="#features" className="hover:text-zinc-300 transition-colors">Features</a>
            <a href="#pricing" className="hover:text-zinc-300 transition-colors">Pricing</a>
            <a href="#faq" className="hover:text-zinc-300 transition-colors">FAQ</a>
            <button onClick={() => openLegalModal('TERMS')} className="hover:text-zinc-300 transition-colors text-zinc-400">
              {isKo ? '서비스이용약관' : 'Terms'}
            </button>
            <button onClick={() => openLegalModal('PRIVACY')} className="hover:text-zinc-300 transition-colors text-zinc-400 font-semibold">
              {isKo ? '개인정보처리방침' : 'Privacy'}
            </button>
            <button onClick={() => openLegalModal('COMPANY')} className="hover:text-cyan-400 transition-colors text-cyan-400/80">
              {isKo ? '사업자정보' : 'Company'}
            </button>
            <button onClick={() => openLegalModal('DISCLAIMER')} className="hover:text-amber-400 transition-colors text-amber-500/80">
              {isKo ? '금융면책고지' : 'Disclaimer'}
            </button>
            <button onClick={() => onOpenAuth('login')} className="hover:text-zinc-300 transition-colors">Sign In</button>
          </div>
        </div>

        {/* E-Commerce Business Info (통신판매업 필수 고지) */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6 pt-6 border-t border-white/5 text-[11px] text-zinc-500 leading-relaxed font-sans">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-zinc-400">
            <div>
              <p>
                <strong>상호명:</strong> {companyInfo?.companyName || '에이드릭스 (Aidrix Inc.)'} &nbsp;|&nbsp; <strong>대표자:</strong> {companyInfo?.representative || '이지훈'} &nbsp;|&nbsp; <strong>사업자등록번호:</strong> {companyInfo?.businessRegistrationNumber || '219-19-68192'}
              </p>
              <p className="mt-0.5">
                <strong>통신판매업 신고번호:</strong> {companyInfo?.mailOrderSalesRegistrationNumber || '신고 준비 중'} &nbsp;|&nbsp; <strong>사업장 소재지:</strong> {companyInfo?.address || '서울시 서초구 사임당로'}
              </p>
            </div>
            <div className="md:text-right">
              <p>
                <strong>고객지원:</strong> {companyInfo?.email || 'ceo@aidrix.io'}
                {companyInfo?.phone ? (
                  <> &nbsp;|&nbsp; <strong>전화:</strong> {companyInfo.phone}</>
                ) : null}
                &nbsp;|&nbsp; <strong>호스팅:</strong> {companyInfo?.hostingProvider || 'Cloudflare, Inc.'}
              </p>
              <p className="mt-0.5">
                <strong>개인정보 보호책임자:</strong> {companyInfo?.cpo?.name || '이지훈'} ({companyInfo?.cpo?.email || 'ceo@aidrix.io'})
                {companyInfo?.cpo?.phone ? (
                  <> &nbsp;|&nbsp; <strong>전화:</strong> {companyInfo.cpo.phone}</>
                ) : null}
              </p>
            </div>
          </div>
        </div>

        {/* Financial Disclaimer (금융/투자 데이터 솔루션 특화 면책 공지) */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4 pt-4 border-t border-white/5 text-[11px] text-zinc-500 leading-relaxed font-sans">
          <div className="p-3 rounded-lg bg-white/[0.02] border border-white/5 space-y-1">
            <p className="text-zinc-300 font-medium">
              {companyInfo?.financialDisclaimerSummary || (isKo
                ? '[금융투자 데이터 솔루션 면책 공지] 본 서비스가 제공하는 데이터 및 지표는 투자 참고용 정보이며, 특정 종목에 대한 투자 권유나 자문이 아니므로 투자 손익에 대한 법적 책임은 사용자 본인에게 있습니다.'
                : '[Financial Disclaimer] All data and metrics provided by EZOptix are for reference only, do not constitute investment advice or a solicitation to trade securities, and legal responsibility for investment outcomes rests solely with the user.')}
            </p>
            <p className="text-[10px] text-zinc-500">
              {isKo
                ? 'EZOptix는 「자본시장과 금융투자업에 관한 법률」상 금융투자업자 및 유사투자자문업자가 아니며, 순수한 정량 데이터 시각화 및 지표 계산 도구(SaaS)를 제공합니다. 시스템이 제공하는 과거 통계 지표와 AI 분석 결과는 미래 수익을 보장하지 않으며, 모든 투자 결정과 원금 손실 위험에 대한 책임은 전적으로 회원 본인에게 귀속됩니다.'
                : 'EZOptix is a quantitative SaaS data tool and does not provide regulated advisory services. Past performance does not guarantee future results.'}
            </p>
          </div>
        </div>
      </footer>

      {/* 종합 법적 고지 모달 */}
      <LegalNoticeModal
        isOpen={isLegalModalOpen}
        onClose={() => setIsLegalModalOpen(false)}
        initialTab={legalModalTab}
        uiLanguage={uiLanguage}
      />
    </div>
  );
}
